import { FC, useState } from "react";
import { useSelector } from "react-redux";
import LightTooltip from "../../../SolarGikLib/tooltip/LightTooltip";
import { RootState } from "../../app/Store";
import { selectFieldState, selectSafetyReason } from "../../app/store/MultisiteTagsStore";
import { formatTimeForTooltip, NO_DATA_FOR_TIME } from "../DataPointTooltipContent";
import getIcons from "../../../SolarGikLib/icons/Icons";
import { IconCategory, ReasonIcons } from "../../../SolarGikLib/icons/IconsModels";
import {
  FieldStateTooltipContent,
  ISafetyReasonTooltipItem,
} from "../../../SolarGikLib/tooltip/FieldStateTooltipContent";
import { useInterval } from "../../../common/Hooks/useInterval";
import { SafetyReason } from "../models/TagsModels";

export interface FieldStateTooltipProps {
  siteId: string;
  children?: React.ReactNode;
  onOpen?: () => void;
}

const FieldStateTooltip: FC<FieldStateTooltipProps> = ({ onOpen, children, siteId }) => {
  return (
    <LightTooltip onOpen={onOpen} title={<FieldStateTooltipData siteId={siteId} />}>
      <div>{children}</div>
    </LightTooltip>
  );
};

const FieldStateTooltipData: FC<{ siteId: string }> = ({ siteId }) => {
  const safetyReason = useSelector((state: RootState) => selectSafetyReason(state, siteId));

  const fieldState = useSelector((state: RootState) => selectFieldState(state, siteId));

  const updateFormattedTime = () => formatTimeForTooltip(fieldState.time, false);

  const [formattedTime, setFormattedTime] = useState(updateFormattedTime);
  useInterval(() => setFormattedTime(updateFormattedTime()), 1000);
  const timestampMessage = formattedTime === NO_DATA_FOR_TIME ? "" : `Last Updated: ${formattedTime}`;
  const tooltipInfo: ISafetyReasonTooltipItem[] = [];
  const safteyReasonsList = getSafetyReasonInfo(safetyReason);
  if (safteyReasonsList.length == 0) {
    return <FieldStateTooltipContent name={""} info={tooltipInfo} timestamp={timestampMessage} />;
  }
  tooltipInfo.push(...safteyReasonsList);
  return (
    <FieldStateTooltipContent name={"Field State"} info={tooltipInfo} timestamp={timestampMessage} />
  );
};

const windIcon = getIcons(ReasonIcons.Wind, IconCategory.SafetyReason);
const snowIcon = getIcons(ReasonIcons.Snow, IconCategory.SafetyReason);
const upsIcon = getIcons(ReasonIcons.Ups, IconCategory.SafetyReason);
const userIcon = getIcons(ReasonIcons.User, IconCategory.SafetyReason);
const engineerIcon = getIcons(ReasonIcons.Engineer, IconCategory.SafetyReason);
const forecastIcon = getIcons(ReasonIcons.WindForecastSafety, IconCategory.SafetyReason);
const snowForecastIcon = getIcons(ReasonIcons.SnowForecastSafety, IconCategory.SafetyReason);

interface ISafetyReasonMetadata extends ISafetyReasonTooltipItem {
  shortText: string;
}

const reasons: { [key in SafetyReason]?: ISafetyReasonMetadata } = {
  [SafetyReason.WindSafe]: { text: "Wind stow", shortText: "Wind", icon: windIcon },
  [SafetyReason.UserSafe]: { text: "User initiated stow", shortText: "User", icon: userIcon },
  [SafetyReason.EngineerSafe]: {
    text: "Engineer initiated stow",
    shortText: "Engineer",
    icon: engineerIcon,
  },
  [SafetyReason.UpsSafe]: { text: "Power loss (UPS) stow", shortText: "UPS", icon: upsIcon },
  [SafetyReason.UpsRecoverySafe]: {
    text: "Recovering from power loss (UPS)",
    shortText: "UPS recovery",
    icon: upsIcon,
  },
  [SafetyReason.SnowSafe]: { text: "Snow stow", shortText: "Snow", icon: snowIcon },
  [SafetyReason.WindForecastSafe]: {
    text: "Wind forecast stow",
    shortText: "Wind forecast",
    icon: forecastIcon,
  },
  [SafetyReason.SnowForecastSafe]: {
    text: "Snow forecast stow",
    shortText: "Snow forecast",
    icon: snowForecastIcon,
  },
  [SafetyReason.ExternalScadaSafe]: {
    text: "External SCADA stow",
    shortText: "External SCADA",
  },
  [SafetyReason.TrackingUnauthorizationSafe]: {
    text: "Tracking unauthorized stow",
    shortText: "Tracking unauthorized",
  },
};

export const getSafetyReasonInfo = (flags: number): ISafetyReasonTooltipItem[] => {
  if (flags <= 0) {
    return [];
  }
  const texts: ISafetyReasonTooltipItem[] = [];
  for (const [key, item] of Object.entries(reasons)) {
    const reasonFlag = Number.parseInt(key, 10);
    if ((flags & reasonFlag) !== 0 && item) {
      texts.push({ text: item.text, icon: item.icon });
    }
  }

  return texts;
};

export const getPrimarySafetyReasonShortText = (flags: number): string | undefined => {
  if (flags <= 0) {
    return undefined;
  }
  for (const [key, item] of Object.entries(reasons)) {
    const reasonFlag = Number.parseInt(key, 10);
    if ((flags & reasonFlag) !== 0 && item) {
      return item.shortText;
    }
  }
  return undefined;
};

export default FieldStateTooltip;
